/**
 * Fall Risk Dashboard
 * 
 * Admin-only dashboard showing:
 * - Total falls across census with trend charts
 * - High-risk patients list
 * - Fall prevention effectiveness metrics
 * - Location/time analysis
 * - Injury severity breakdown
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  AlertTriangle, TrendingUp, TrendingDown, Users, Activity,
  MapPin, Clock, Shield, RefreshCw, Loader2, ChevronRight,
  BarChart3, PieChart, Target, ArrowUp, ArrowDown, Minus
} from "lucide-react";
import api from "@/lib/api";

// Location labels
const LOCATION_LABELS = {
  bedroom: "Bedroom",
  bathroom: "Bathroom",
  living_room: "Living Room",
  kitchen: "Kitchen",
  hallway: "Hallway",
  stairs: "Stairs",
  outdoor: "Outdoor",
  other: "Other"
};

// Time period labels
const TIME_PERIODS = {
  morning: "Morning (6AM-12PM)",
  afternoon: "Afternoon (12PM-6PM)",
  evening: "Evening (6PM-12AM)",
  night: "Night (12AM-6AM)"
};

export default function FallRiskDashboard({ userRole }) {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [timeRange, setTimeRange] = useState("30");
  const [dashboard, setDashboard] = useState(null);

  // Check admin access
  const isAdmin = userRole?.toLowerCase() === "admin" || userRole?.toLowerCase() === "administrator";

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/clinical/fall-events/dashboard?days=${timeRange}`);
      setDashboard(response.data);
    } catch (err) {
      // Demo data
      setDashboard({
        summary: {
          total_falls: 24,
          falls_with_injury: 6,
          er_transfers: 2,
          patients_affected: 18,
          fall_rate: 3.2, // per 1000 patient days
          prevention_rate: 78
        },
        trends: {
          current_period: 24,
          previous_period: 28,
          change_percent: -14,
          trend: "decreasing"
        },
        by_location: [
          { location: "bathroom", count: 8, percentage: 33 },
          { location: "bedroom", count: 6, percentage: 25 },
          { location: "hallway", count: 4, percentage: 17 },
          { location: "living_room", count: 3, percentage: 13 },
          { location: "other", count: 3, percentage: 12 }
        ],
        by_time: [
          { period: "night", count: 9, percentage: 38 },
          { period: "morning", count: 7, percentage: 29 },
          { period: "evening", count: 5, percentage: 21 },
          { period: "afternoon", count: 3, percentage: 12 }
        ],
        by_injury: [
          { type: "none", count: 18, percentage: 75 },
          { type: "minor", count: 4, percentage: 17 },
          { type: "moderate", count: 1, percentage: 4 },
          { type: "severe", count: 1, percentage: 4 }
        ],
        high_risk_patients: [
          { id: "p1", name: "John Smith", falls_count: 3, last_fall: "2025-01-15", risk_level: "high" },
          { id: "p2", name: "Mary Johnson", falls_count: 2, last_fall: "2025-01-18", risk_level: "high" },
          { id: "p3", name: "Robert Williams", falls_count: 2, last_fall: "2025-01-10", risk_level: "medium" },
          { id: "p4", name: "Patricia Brown", falls_count: 1, last_fall: "2025-01-20", risk_level: "medium" }
        ],
        contributing_factors: [
          { factor: "weakness", count: 15, percentage: 62 },
          { factor: "dizziness", count: 10, percentage: 42 },
          { factor: "confusion", count: 8, percentage: 33 },
          { factor: "medication", count: 6, percentage: 25 },
          { factor: "environment", count: 4, percentage: 17 }
        ],
        prevention_metrics: {
          patients_with_fall_plan: 142,
          total_at_risk: 156,
          interventions_implemented: 89,
          equipment_orders: 34,
          home_safety_assessments: 67
        }
      });
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const getTrendIcon = (trend) => {
    switch (trend) {
      case "increasing": return <ArrowUp className="w-4 h-4 text-red-400" />;
      case "decreasing": return <ArrowDown className="w-4 h-4 text-emerald-400" />;
      default: return <Minus className="w-4 h-4 text-slate-400" />;
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case "high": return "bg-red-500/20 text-red-400 border-red-500/50";
      case "medium": return "bg-amber-500/20 text-amber-400 border-amber-500/50";
      default: return "bg-emerald-500/20 text-emerald-400 border-emerald-500/50";
    }
  };

  const getLocationColor = (idx) => {
    const colors = [
      "[&>div]:bg-red-500",
      "[&>div]:bg-amber-500",
      "[&>div]:bg-blue-500",
      "[&>div]:bg-purple-500",
      "[&>div]:bg-slate-500"
    ];
    return colors[idx % colors.length];
  };

  // Access check
  if (!isAdmin) {
    return (
      <Card className="bg-slate-900 border-slate-700">
        <CardContent className="pt-6 text-center">
          <AlertTriangle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Administrator Access Required</h3>
          <p className="text-slate-400">
            The Fall Risk Dashboard is restricted to administrators only.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="fall-risk-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <AlertTriangle className="w-7 h-7 text-amber-400" />
            Fall Risk Dashboard
          </h2>
          <p className="text-slate-400 mt-1">
            Census-wide fall analytics and risk management
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-40 bg-slate-800 border-slate-600">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-600">
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchDashboard} className="border-slate-600">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-6 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
              <AlertTriangle className="w-4 h-4" />
              Total Falls
            </div>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold text-white">{dashboard?.summary?.total_falls}</span>
              <div className="flex items-center gap-1">
                {getTrendIcon(dashboard?.trends?.trend)}
                <span className={`text-xs ${dashboard?.trends?.change_percent < 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {dashboard?.trends?.change_percent}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-red-900/30 border-red-500/30">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-red-400 text-xs mb-1">
              <Activity className="w-4 h-4" />
              With Injury
            </div>
            <span className="text-2xl font-bold text-red-400">{dashboard?.summary?.falls_with_injury}</span>
          </CardContent>
        </Card>

        <Card className="bg-amber-900/30 border-amber-500/30">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-amber-400 text-xs mb-1">
              <Shield className="w-4 h-4" />
              ER Transfers
            </div>
            <span className="text-2xl font-bold text-amber-400">{dashboard?.summary?.er_transfers}</span>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
              <Users className="w-4 h-4" />
              Patients Affected
            </div>
            <span className="text-2xl font-bold text-white">{dashboard?.summary?.patients_affected}</span>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
              <BarChart3 className="w-4 h-4" />
              Fall Rate
            </div>
            <span className="text-2xl font-bold text-white">{dashboard?.summary?.fall_rate}</span>
            <span className="text-xs text-slate-500 ml-1">per 1000 pt-days</span>
          </CardContent>
        </Card>

        <Card className="bg-emerald-900/30 border-emerald-500/30">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-emerald-400 text-xs mb-1">
              <Target className="w-4 h-4" />
              Prevention Rate
            </div>
            <span className="text-2xl font-bold text-emerald-400">{dashboard?.summary?.prevention_rate}%</span>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800">
          <TabsTrigger value="overview" className="data-[state=active]:bg-amber-600">
            <BarChart3 className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="high_risk" className="data-[state=active]:bg-red-600">
            <Users className="w-4 h-4 mr-2" />
            High Risk Patients
          </TabsTrigger>
          <TabsTrigger value="analysis" className="data-[state=active]:bg-blue-600">
            <PieChart className="w-4 h-4 mr-2" />
            Analysis
          </TabsTrigger>
          <TabsTrigger value="prevention" className="data-[state=active]:bg-emerald-600">
            <Shield className="w-4 h-4 mr-2" />
            Prevention
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Falls by Location */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-400" />
                  Falls by Location
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {dashboard?.by_location?.map((item, idx) => (
                  <div key={item.location} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-300">{LOCATION_LABELS[item.location] || item.location}</span>
                      <span className="text-white font-medium">{item.count} ({item.percentage}%)</span>
                    </div>
                    <Progress value={item.percentage} className={`h-2 ${getLocationColor(idx)}`} />
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Falls by Time */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Clock className="w-5 h-5 text-purple-400" />
                  Falls by Time of Day
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {dashboard?.by_time?.map((item, idx) => (
                  <div key={item.period} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-300">{TIME_PERIODS[item.period] || item.period}</span>
                      <span className="text-white font-medium">{item.count} ({item.percentage}%)</span>
                    </div>
                    <Progress 
                      value={item.percentage} 
                      className={`h-2 ${
                        item.period === 'night' ? '[&>div]:bg-indigo-500' :
                        item.period === 'morning' ? '[&>div]:bg-amber-500' :
                        item.period === 'afternoon' ? '[&>div]:bg-orange-500' :
                        '[&>div]:bg-purple-500'
                      }`}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* High Risk Patients Tab */}
        <TabsContent value="high_risk" className="mt-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg text-white">High Risk Patients</CardTitle>
              <CardDescription className="text-slate-400">
                Patients with multiple falls or recent fall history
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {dashboard?.high_risk_patients?.map((patient, idx) => (
                    <Card key={patient.id} className="bg-slate-800 border-slate-700">
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-white">{patient.name}</span>
                              <Badge variant="outline" className={getRiskColor(patient.risk_level)}>
                                {patient.risk_level} risk
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-400 mt-1">
                              Last fall: {new Date(patient.last_fall).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold text-white">{patient.falls_count}</div>
                            <div className="text-xs text-slate-500">falls in period</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analysis Tab */}
        <TabsContent value="analysis" className="mt-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Injury Severity */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg text-white">Injury Severity Distribution</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {dashboard?.by_injury?.map((item) => (
                  <div key={item.type} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className={
                        item.type === 'severe' ? 'bg-red-500/20 text-red-400 border-red-500/50' :
                        item.type === 'moderate' ? 'bg-orange-500/20 text-orange-400 border-orange-500/50' :
                        item.type === 'minor' ? 'bg-amber-500/20 text-amber-400 border-amber-500/50' :
                        'bg-emerald-500/20 text-emerald-400 border-emerald-500/50'
                      }>
                        {item.type === 'none' ? 'No Injury' : item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                      </Badge>
                    </div>
                    <div className="text-right">
                      <span className="text-white font-medium">{item.count}</span>
                      <span className="text-slate-500 text-sm ml-2">({item.percentage}%)</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Contributing Factors */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg text-white">Contributing Factors</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {dashboard?.contributing_factors?.map((item) => (
                  <div key={item.factor} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-300 capitalize">{item.factor}</span>
                      <span className="text-white font-medium">{item.count} ({item.percentage}%)</span>
                    </div>
                    <Progress value={item.percentage} className="h-2 [&>div]:bg-teal-500" />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Prevention Tab */}
        <TabsContent value="prevention" className="mt-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-emerald-400" />
                Prevention Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-emerald-900/20 border border-emerald-500/30 rounded-lg">
                  <p className="text-sm text-slate-400">Patients with Fall Plan</p>
                  <div className="flex items-end gap-2 mt-1">
                    <span className="text-3xl font-bold text-emerald-400">
                      {dashboard?.prevention_metrics?.patients_with_fall_plan}
                    </span>
                    <span className="text-slate-500 mb-1">
                      / {dashboard?.prevention_metrics?.total_at_risk} at-risk
                    </span>
                  </div>
                  <Progress 
                    value={(dashboard?.prevention_metrics?.patients_with_fall_plan / dashboard?.prevention_metrics?.total_at_risk) * 100} 
                    className="h-2 mt-2 [&>div]:bg-emerald-500" 
                  />
                </div>

                <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                  <p className="text-sm text-slate-400">Interventions Implemented</p>
                  <span className="text-3xl font-bold text-blue-400">
                    {dashboard?.prevention_metrics?.interventions_implemented}
                  </span>
                </div>

                <div className="p-4 bg-purple-900/20 border border-purple-500/30 rounded-lg">
                  <p className="text-sm text-slate-400">Equipment Orders</p>
                  <span className="text-3xl font-bold text-purple-400">
                    {dashboard?.prevention_metrics?.equipment_orders}
                  </span>
                </div>

                <div className="p-4 bg-amber-900/20 border border-amber-500/30 rounded-lg">
                  <p className="text-sm text-slate-400">Home Safety Assessments</p>
                  <span className="text-3xl font-bold text-amber-400">
                    {dashboard?.prevention_metrics?.home_safety_assessments}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export { FallRiskDashboard };
